import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Barcode,
  CheckCircle2,
  Package,
  Plus,
  Wrench,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";

import { Button, Card } from "@/components/ui";
import {
  createCategory,
  createProduct,
  listCategories,
  listTaxes,
  listUnits,
  type CategoryPublic,
  type ProductCreate,
  type ProductPublic,
  type TaxPublic,
  type UnitPublic,
} from "@/lib/endpoints";
import { listWarehouses, recordMovement, type WarehousePublic } from "@/lib/inventory";
import { getSession } from "@/lib/session";

export interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProductCreated?: (product: ProductPublic) => void;
  initialKind?: "producto" | "servicio";
}

export function ProductModal({
  isOpen,
  onClose,
  onProductCreated,
  initialKind = "producto",
}: ProductModalProps) {
  const queryClient = useQueryClient();
  const session = getSession();
  const branchId = session?.branches?.[0]?.id ?? "";

  // Form state
  const [kind, setKind] = useState<"producto" | "servicio">(initialKind);
  const [name, setName] = useState("");
  const [pricePesos, setPricePesos] = useState("");
  const [barcode, setBarcode] = useState("");
  const [sku, setSku] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [uomId, setUomId] = useState("");
  const [taxRateId, setTaxRateId] = useState("");
  const [brand, setBrand] = useState("");
  const [description, setDescription] = useState("");
  const [trackInventory, setTrackInventory] = useState(initialKind === "producto");

  // Stock inicial
  const [enableInitialStock, setEnableInitialStock] = useState(false);
  const [initialWarehouseId, setInitialWarehouseId] = useState("");
  const [initialQty, setInitialQty] = useState("");
  const [initialCostPesos, setInitialCostPesos] = useState("");

  // Categoría rápida
  const [showQuickCategory, setShowQuickCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Queries
  const { data: categories = [] } = useQuery<CategoryPublic[]>({
    queryKey: ["categories"],
    queryFn: () => listCategories(true),
    enabled: isOpen,
  });

  const { data: units = [] } = useQuery<UnitPublic[]>({
    queryKey: ["units"],
    queryFn: () => listUnits(true),
    enabled: isOpen,
  });

  const { data: taxes = [] } = useQuery<TaxPublic[]>({
    queryKey: ["taxes"],
    queryFn: () => listTaxes(true),
    enabled: isOpen,
  });

  const { data: warehouses = [] } = useQuery<WarehousePublic[]>({
    queryKey: ["inventory", "warehouses", branchId],
    queryFn: () => listWarehouses(branchId),
    enabled: isOpen && Boolean(branchId),
  });

  // Al abrir el modal o cambiar el tipo se ajustan defaults
  useEffect(() => {
    if (isOpen) {
      setKind(initialKind);
      setTrackInventory(initialKind === "producto");
      setErrorMessage(null);
    }
  }, [isOpen, initialKind]);

  useEffect(() => {
    if (!initialWarehouseId && warehouses.length > 0) {
      const defaultWh = warehouses.find((w) => w.is_default) ?? warehouses[0];
      if (defaultWh) setInitialWarehouseId(defaultWh.id);
    }
  }, [warehouses, initialWarehouseId]);

  useEffect(() => {
    if (!taxRateId && taxes.length > 0) {
      const defaultTax = taxes.find((t) => t.is_default) ?? taxes[0];
      if (defaultTax) setTaxRateId(defaultTax.id);
    }
  }, [taxes, taxRateId]);

  useEffect(() => {
    if (!uomId && units.length > 0) {
      if (kind === "servicio") {
        const srvUnit = units.find((u) => u.code === "E48" || u.code.toLowerCase().includes("serv"));
        if (srvUnit) setUomId(srvUnit.id);
      } else {
        const pzaUnit = units.find((u) => u.code === "H87" || u.code.toLowerCase().includes("pza"));
        if (pzaUnit) setUomId(pzaUnit.id);
      }
    }
  }, [units, kind, uomId]);

  // Mutación para crear categoría rápida
  const categoryMutation = useMutation({
    mutationFn: (catName: string) => createCategory({ name: catName.trim(), sort_order: 0 }),
    onSuccess: (newCat) => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      setCategoryId(newCat.id);
      setShowQuickCategory(false);
      setNewCategoryName("");
    },
    onError: (err: Error) => {
      setErrorMessage(err.message || "Error al crear la categoría");
    },
  });

  // Mutación para crear producto
  const productMutation = useMutation({
    mutationFn: async () => {
      const cleanName = name.trim();
      if (!cleanName) {
        throw new Error("El nombre del producto o servicio es obligatorio.");
      }

      const parsedPrice = parseFloat(pricePesos);
      if (isNaN(parsedPrice) || parsedPrice < 0) {
        throw new Error("Ingresa un precio de venta válido mayor o igual a 0.");
      }

      const priceCents = Math.round(parsedPrice * 100);

      const payload: ProductCreate = {
        kind,
        name: cleanName,
        description: description.trim() || null,
        category_id: categoryId || null,
        uom_id: uomId || null,
        tax_rate_id: taxRateId || null,
        brand: brand.trim() || null,
        track_inventory: trackInventory,
        sku: sku.trim() || null,
        price_cents: priceCents,
        barcode: barcode.trim() || null,
      };

      const product = await createProduct(payload);

      // Si se configuró existencia inicial y es producto con inventario
      if (trackInventory && enableInitialStock) {
        const qty = parseFloat(initialQty);
        if (!isNaN(qty) && qty > 0) {
          const whId = initialWarehouseId || warehouses[0]?.id;
          if (!whId) {
            throw new Error("No hay un almacén seleccionado para la existencia inicial.");
          }

          const defaultVariant = product.variants?.[0];
          if (!defaultVariant) {
            throw new Error("El producto creado no tiene una presentación predeterminada.");
          }

          const costPesos = parseFloat(initialCostPesos);
          const unitCostCents = !isNaN(costPesos) && costPesos >= 0 ? Math.round(costPesos * 100) : null;

          await recordMovement({
            warehouse_id: whId,
            variant_id: defaultVariant.id,
            quantity: qty,
            unit_cost_cents: unitCostCents,
            kind: "entrada",
            reason: "Inventario inicial al dar de alta el producto",
          });
        }
      }

      return product;
    },
    onSuccess: (createdProduct) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      queryClient.invalidateQueries({ queryKey: ["search"] });

      // Reset form
      setName("");
      setPricePesos("");
      setBarcode("");
      setSku("");
      setBrand("");
      setDescription("");
      setInitialQty("");
      setInitialCostPesos("");
      setEnableInitialStock(false);
      setErrorMessage(null);

      onProductCreated?.(createdProduct);
      onClose();
    },
    onError: (err: Error) => {
      setErrorMessage(err.message || "Ocurrió un error al guardar el producto.");
    },
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm overflow-y-auto">
      <Card className="w-full max-w-2xl p-6 space-y-4 my-8 max-h-[92vh] flex flex-col">
        {/* Cabecera */}
        <div className="flex items-center justify-between border-b border-line pb-3">
          <div className="flex items-center gap-2.5">
            <div className="rounded-msr bg-electric/10 p-2 text-electric">
              {kind === "producto" ? <Package className="size-5" /> : <Wrench className="size-5" />}
            </div>
            <div>
              <h2 className="font-display text-lg font-bold text-carbon">
                Registrar Nuevo {kind === "producto" ? "Producto" : "Servicio"}
              </h2>
              <p className="text-xs text-muted">
                Dará de alta el artículo en el catálogo y listas de precios para su venta inmediata.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted hover:text-carbon transition-colors"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Mensaje de Error */}
        {errorMessage ? (
          <div className="rounded-msr bg-rose-50 border border-rose-200 p-3 text-xs text-rose-800">
            {errorMessage}
          </div>
        ) : null}

        {/* Formulario */}
        <form
          id="product-form"
          onSubmit={(e) => {
            e.preventDefault();
            productMutation.mutate();
          }}
          className="space-y-4 overflow-y-auto pr-1 flex-1 text-sm text-carbon"
        >
          {/* Selector de Tipo (Pill / Segmented) */}
          <div>
            <label className="block text-xs font-semibold text-muted uppercase tracking-wider mb-1.5">
              Tipo de Artículo *
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => {
                  setKind("producto");
                  setTrackInventory(true);
                }}
                className={`flex items-center justify-center gap-2 rounded-msr border py-2.5 px-3 text-xs font-bold transition-all ${
                  kind === "producto"
                    ? "border-electric bg-electric/10 text-electric shadow-xs"
                    : "border-line bg-snow text-muted hover:text-carbon"
                }`}
              >
                <Package className="size-4" />
                Producto Físico (con inventario)
              </button>
              <button
                type="button"
                onClick={() => {
                  setKind("servicio");
                  setTrackInventory(false);
                  setEnableInitialStock(false);
                }}
                className={`flex items-center justify-center gap-2 rounded-msr border py-2.5 px-3 text-xs font-bold transition-all ${
                  kind === "servicio"
                    ? "border-electric bg-electric/10 text-electric shadow-xs"
                    : "border-line bg-snow text-muted hover:text-carbon"
                }`}
              >
                <Wrench className="size-4" />
                Servicio / Mano de Obra
              </button>
            </div>
          </div>

          {/* Nombre y Precio */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-muted mb-1">
                Nombre del {kind === "producto" ? "Producto" : "Servicio"} *
              </label>
              <input
                type="text"
                required
                placeholder={kind === "producto" ? "Ej. Aceite Sintético 5W-30 1L" : "Ej. Cambio de Aceite y Filtro"}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon placeholder:text-muted focus:border-electric focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted mb-1">
                Precio de Venta ($ MXN) *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted">
                  $
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  placeholder="0.00"
                  value={pricePesos}
                  onChange={(e) => setPricePesos(e.target.value)}
                  className="w-full rounded-msr border border-line bg-snow py-2 pl-7 pr-3 text-sm font-semibold text-carbon placeholder:text-muted focus:border-electric focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Código de Barras y SKU */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-muted mb-1">
                Código de Barras (Opcional)
              </label>
              <div className="relative">
                <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted" />
                <input
                  type="text"
                  placeholder="Escanear o escribir código..."
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  className="w-full rounded-msr border border-line bg-snow py-2 pl-9 pr-3 text-sm text-carbon placeholder:text-muted focus:border-electric focus:outline-none font-mono text-xs"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted mb-1">
                SKU / Código Interno (Opcional)
              </label>
              <input
                type="text"
                placeholder="Se generará automáticamente si lo dejas vacío"
                value={sku}
                onChange={(e) => setSku(e.target.value.toUpperCase())}
                className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon placeholder:text-muted focus:border-electric focus:outline-none font-mono text-xs"
              />
            </div>
          </div>

          {/* Categoría, Unidad e Impuesto */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-muted">Categoría</label>
                <button
                  type="button"
                  onClick={() => setShowQuickCategory(!showQuickCategory)}
                  className="text-[11px] font-bold text-electric hover:underline inline-flex items-center gap-0.5"
                >
                  <Plus className="size-3" /> Nueva
                </button>
              </div>

              {showQuickCategory ? (
                <div className="flex items-center gap-1.5 mb-1">
                  <input
                    type="text"
                    placeholder="Nombre categoría..."
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    className="flex-1 rounded-msr border border-line bg-snow px-2 py-1 text-xs text-carbon focus:border-electric focus:outline-none"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="primary"
                    disabled={!newCategoryName.trim() || categoryMutation.isPending}
                    onClick={() => categoryMutation.mutate(newCategoryName)}
                    className="h-7 text-xs px-2"
                  >
                    OK
                  </Button>
                </div>
              ) : null}

              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-xs font-medium text-carbon focus:border-electric focus:outline-none"
              >
                <option value="">(Sin categoría)</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted mb-1">Unidad de Medida</label>
              <select
                value={uomId}
                onChange={(e) => setUomId(e.target.value)}
                className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-xs font-medium text-carbon focus:border-electric focus:outline-none"
              >
                <option value="">(Por defecto)</option>
                {units.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.code})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted mb-1">Tasa Impuesto (IVA)</label>
              <select
                value={taxRateId}
                onChange={(e) => setTaxRateId(e.target.value)}
                className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-xs font-medium text-carbon focus:border-electric focus:outline-none"
              >
                <option value="">(Por defecto)</option>
                {taxes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.code})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Marca y Descripción */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-muted mb-1">Marca (Opcional)</label>
              <input
                type="text"
                placeholder="Ej. Mobil, Bosch, Coca-Cola"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon placeholder:text-muted focus:border-electric focus:outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-muted mb-1">Descripción (Opcional)</label>
              <input
                type="text"
                placeholder="Notas o detalles del producto..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon placeholder:text-muted focus:border-electric focus:outline-none"
              />
            </div>
          </div>

          {/* Control de Inventario y Existencias Iniciales (solo para productos físicos) */}
          {kind === "producto" ? (
            <div className="rounded-msr border border-line bg-surface p-3.5 space-y-3">
              <label className="flex items-center gap-2 text-xs font-semibold text-carbon cursor-pointer">
                <input
                  type="checkbox"
                  checked={trackInventory}
                  onChange={(e) => {
                    setTrackInventory(e.target.checked);
                    if (!e.target.checked) setEnableInitialStock(false);
                  }}
                  className="rounded border-line text-electric focus:ring-electric"
                />
                Llevar control de existencias en almacenes (Kardex)
              </label>

              {trackInventory ? (
                <div className="pt-2 border-t border-line/60 space-y-2">
                  <label className="flex items-center gap-2 text-xs font-medium text-muted cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enableInitialStock}
                      onChange={(e) => setEnableInitialStock(e.target.checked)}
                      className="rounded border-line text-electric focus:ring-electric"
                    />
                    <span>¿Registrar existencias iniciales ahora?</span>
                  </label>

                  {enableInitialStock ? (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-1">
                      <div>
                        <label className="block text-[11px] font-semibold text-muted mb-1">
                          Almacén de Entrada *
                        </label>
                        <select
                          value={initialWarehouseId}
                          onChange={(e) => setInitialWarehouseId(e.target.value)}
                          className="w-full rounded-msr border border-line bg-snow px-2.5 py-1.5 text-xs text-carbon focus:border-electric focus:outline-none"
                        >
                          {warehouses.map((w) => (
                            <option key={w.id} value={w.id}>
                              {w.name} ({w.code})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-[11px] font-semibold text-muted mb-1">
                          Cantidad Inicial *
                        </label>
                        <input
                          type="number"
                          step="any"
                          min="0.0001"
                          placeholder="Ej. 10"
                          value={initialQty}
                          onChange={(e) => setInitialQty(e.target.value)}
                          className="w-full rounded-msr border border-line bg-snow px-2.5 py-1.5 text-xs text-carbon focus:border-electric focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-semibold text-muted mb-1">
                          Costo Unitario ($ MXN)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Ej. 75.00"
                          value={initialCostPesos}
                          onChange={(e) => setInitialCostPesos(e.target.value)}
                          className="w-full rounded-msr border border-line bg-snow px-2.5 py-1.5 text-xs text-carbon focus:border-electric focus:outline-none"
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </form>

        {/* Botones de Pie */}
        <div className="flex items-center justify-end gap-3 border-t border-line pt-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form="product-form"
            loading={productMutation.isPending}
            className="flex items-center gap-1.5"
          >
            <CheckCircle2 className="size-4" />
            Guardar {kind === "producto" ? "Producto" : "Servicio"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
